import React from 'react';
import { ToolPageSectionFields } from '../toolPageEditor/ToolPageSectionFields';
import RichTextField, { RichTextParagraphsField } from '../toolPageEditor/RichTextField';
import CustomTablesSectionFields from './CustomTablesSectionFields';

const GENERIC_TYPES = new Set(['description', 'video', 'faq', 'cta', 'steps', 'platformVideos']);

function ListField({ label, items, onChange, renderItem, onAdd, addLabel }) {
  return (
    <div className="tpb-field-group">
      <div className="tpb-field-group-head">
        <strong>{label}</strong>
        <button type="button" className="btn btn-secondary tpb-add-btn" onClick={onAdd}>
          <i className="fas fa-plus" aria-hidden="true" /> {addLabel}
        </button>
      </div>
      {items.map((item, idx) => (
        <div className="tpb-repeat-block" key={idx}>
          {renderItem(item, idx)}
        </div>
      ))}
    </div>
  );
}

export function StreamPageSectionFields({ section, onChange }) {
  if (GENERIC_TYPES.has(section.type)) {
    return <ToolPageSectionFields section={section} onChange={onChange} />;
  }

  const update = (patch) => onChange({ ...section, ...patch });

  switch (section.type) {
    case 'scheduleCalendar':
      return (
        <div className="admin-form">
          <div className="admin-grid-2">
            <label>
              Heading
              <input value={section.heading || ''} onChange={(e) => update({ heading: e.target.value })} />
            </label>
            <label>
              Highlight
              <input value={section.highlight || ''} onChange={(e) => update({ highlight: e.target.value })} />
            </label>
          </div>
          <label>
            Intro
            <textarea value={section.intro || ''} onChange={(e) => update({ intro: e.target.value })} rows={3} />
          </label>
          <label>
            Footnote
            <textarea value={section.footnote || ''} onChange={(e) => update({ footnote: e.target.value })} rows={2} />
          </label>
          <label>
            TBC month note
            <textarea value={section.tbcNote || ''} onChange={(e) => update({ tbcNote: e.target.value })} rows={2} />
          </label>
          <label>
            Special Events note
            <textarea value={section.specialEventsNote || ''} onChange={(e) => update({ specialEventsNote: e.target.value })} rows={4} />
          </label>
          <p className="tpb-sidebar-hint">Game entries are managed in Admin → Schedule Games.</p>
        </div>
      );

    case 'aboutStream':
      return (
        <div className="admin-form">
          <div className="admin-grid-2">
            <label>
              Heading
              <input value={section.heading || ''} onChange={(e) => update({ heading: e.target.value })} />
            </label>
            <label>
              Highlight
              <input value={section.highlight || ''} onChange={(e) => update({ highlight: e.target.value })} />
            </label>
          </div>
          <label>
            YouTube heading
            <input value={section.youtubeHeading || ''} onChange={(e) => update({ youtubeHeading: e.target.value })} />
          </label>
          <RichTextParagraphsField
            label="YouTube paragraphs"
            paragraphs={section.youtubeParagraphs || ['']}
            onChange={(youtubeParagraphs) => update({ youtubeParagraphs })}
          />
          <label>
            Twitch heading
            <input value={section.twitchHeading || ''} onChange={(e) => update({ twitchHeading: e.target.value })} />
          </label>
          <RichTextField label="Twitch paragraph" value={section.twitchParagraph || ''} onChange={(twitchParagraph) => update({ twitchParagraph })} />
          <label>
            Expect heading
            <input value={section.expectHeading || ''} onChange={(e) => update({ expectHeading: e.target.value })} />
          </label>
          <ListField
            label="Expect items"
            items={section.expectItems || []}
            addLabel="Add item"
            onAdd={() => update({ expectItems: [...(section.expectItems || []), { label: '', text: '' }] })}
            renderItem={(item, idx) => (
              <>
                <label>
                  Label
                  <input
                    value={item.label || ''}
                    onChange={(e) => {
                      const expectItems = [...section.expectItems];
                      expectItems[idx] = { ...item, label: e.target.value };
                      update({ expectItems });
                    }}
                  />
                </label>
                <label>
                  Text
                  <textarea
                    value={item.text || ''}
                    rows={2}
                    onChange={(e) => {
                      const expectItems = [...section.expectItems];
                      expectItems[idx] = { ...item, text: e.target.value };
                      update({ expectItems });
                    }}
                  />
                </label>
                <div className="admin-grid-2">
                  <label>
                    Link path (optional)
                    <input
                      value={item.link || ''}
                      onChange={(e) => {
                        const expectItems = [...section.expectItems];
                        expectItems[idx] = { ...item, link: e.target.value };
                        update({ expectItems });
                      }}
                      placeholder="/shop"
                    />
                  </label>
                  <label>
                    Link label
                    <input
                      value={item.linkLabel || ''}
                      onChange={(e) => {
                        const expectItems = [...section.expectItems];
                        expectItems[idx] = { ...item, linkLabel: e.target.value };
                        update({ expectItems });
                      }}
                    />
                  </label>
                </div>
              </>
            )}
          />
          <label>
            Support text
            <textarea value={section.supportText || ''} onChange={(e) => update({ supportText: e.target.value })} rows={2} />
          </label>
        </div>
      );

    case 'features':
      return (
        <div className="admin-form">
          <div className="admin-grid-2">
            <label>
              Title before highlight
              <input value={section.heading || ''} onChange={(e) => update({ heading: e.target.value })} />
            </label>
            <label>
              Highlight
              <input value={section.highlight || ''} onChange={(e) => update({ highlight: e.target.value })} />
            </label>
          </div>
          <ListField
            label="Features"
            items={section.features || []}
            addLabel="Add feature"
            onAdd={() => update({
              features: [...(section.features || []), { icon: 'fas fa-star', iconColor: '#00eaff', title: '', description: '' }],
            })}
            renderItem={(feature, idx) => (
              <>
                <div className="admin-grid-2">
                  <label>
                    Icon class
                    <input
                      value={feature.icon || ''}
                      onChange={(e) => {
                        const features = [...section.features];
                        features[idx] = { ...feature, icon: e.target.value };
                        update({ features });
                      }}
                    />
                  </label>
                  <label>
                    Icon color
                    <input
                      value={feature.iconColor || ''}
                      onChange={(e) => {
                        const features = [...section.features];
                        features[idx] = { ...feature, iconColor: e.target.value };
                        update({ features });
                      }}
                    />
                  </label>
                </div>
                <label>
                  Title
                  <input
                    value={feature.title || ''}
                    onChange={(e) => {
                      const features = [...section.features];
                      features[idx] = { ...feature, title: e.target.value };
                      update({ features });
                    }}
                  />
                </label>
                <label>
                  Description
                  <textarea
                    value={feature.description || ''}
                    rows={2}
                    onChange={(e) => {
                      const features = [...section.features];
                      features[idx] = { ...feature, description: e.target.value };
                      update({ features });
                    }}
                  />
                </label>
              </>
            )}
          />
        </div>
      );

    case 'setup':
      return (
        <div className="admin-form">
          <div className="admin-grid-2">
            <label>
              Heading
              <input value={section.heading || ''} onChange={(e) => update({ heading: e.target.value })} />
            </label>
            <label>
              Highlight
              <input value={section.highlight || ''} onChange={(e) => update({ highlight: e.target.value })} />
            </label>
          </div>
          <label>
            Intro
            <textarea value={section.intro || ''} onChange={(e) => update({ intro: e.target.value })} rows={2} />
          </label>
          <p className="tpb-sidebar-hint">
            Setup categories, PC specs, and communication blocks use JSON in the full page export for complex edits.
            Use the JSON overlay for large structural changes.
          </p>
        </div>
      );

    case 'customTables':
      return <CustomTablesSectionFields section={section} onChange={onChange} />;

    default:
      return <p>Unknown section type.</p>;
  }
}
